import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobal } from "../../global/GlobalContext";

const VerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useGlobal();

  const [status, setStatus] = useState("verifying"); // "verifying" | "success" | "error"
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(4);

  const queryParams = new URLSearchParams(location.search);
  const tokenVerify = queryParams.get("token");
  const email = queryParams.get("email");

  useEffect(() => {
    const verify = async () => {
      if (!tokenVerify || !email) {
        setStatus("error");
        setMessage("Invalid or missing verification link. Please request a new one.");
        return;
      }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/verify_email_verification`,
          { token: tokenVerify, email },
          { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
        );
        if (response.data.status) {
          localStorage.removeItem("uservarified");
          setStatus("success");
        } else {
          setStatus("error");
          setMessage(response.data.message || "Verification failed. The link may have expired.");
        }
      } catch (error) {
        setStatus("error");
        setMessage(error.response?.data?.message || "Something went wrong. Please try again.");
      }
    };
    verify();
  }, []);

  // Countdown and redirect after success
  useEffect(() => {
    if (status !== "success") return;
    if (countdown === 0) {
      navigate("/login");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">

        {/* ── VERIFYING ── */}
        {status === "verifying" && (
          <>
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center">
              <svg className="animate-spin w-12 h-12 text-brand-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying your email</h2>
            <p className="text-gray-500 text-sm">Please wait a moment...</p>
          </>
        )}

        {/* ── SUCCESS ── */}
        {status === "success" && (
          <>
            {/* Animated checkmark circle */}
            <div className="w-24 h-24 mx-auto mb-6 relative">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center animate-[pulse_0.6s_ease-out]">
                <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
            <p className="text-gray-500 text-sm mb-6">
              Your account has been verified successfully. You can now sign in.
            </p>

            {/* Countdown ring */}
            <div className="flex flex-col items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-full border-4 border-green-200 bg-green-50 flex items-center justify-center">
                <span className="text-xl font-bold text-green-600">{countdown}</span>
              </div>
              <p className="text-xs text-gray-400">Redirecting to login in {countdown}s...</p>
            </div>

            <button
              onClick={() => navigate("/login")}
              className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              Go to Login Now
            </button>
          </>
        )}

        {/* ── ERROR ── */}
        {status === "error" && (
          <>
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-gray-500 text-sm mb-6">{message}</p>
            <button
              onClick={() => navigate("/login")}
              className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              Back to Login
            </button>
          </>
        )}

      </div>
    </div>
  );
};

export default VerifyEmail;
